/* eslint-disable custom-eslint-rules/force-logicalOperandsMustBeBoolean */
import { forwardRef, useState } from "react";
import { useTheme } from "styled-components";
import { IconBox } from "../IconBox/IconBox.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { Text } from "../Text/Text.js";
import { TextSize, TextWeight } from "../Text/Text.types.js";
import { TooltipOnTextOverflow } from "../TooltipOnTextOverflow/TooltipOnTextOverflow.js";
import { IconWrapper, StyledTag, TextWrapper } from "./TagV2.style.js";
import type { TagV2Colors, TagV2Props } from "./TagV2.types.js";
import { TagV2ColorsEnum } from "./TagV2.types.js";

const getRandomTagColor = (key: string): TagV2Colors => {
    // Convert the key to a number using a simple hash function
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        hash = key.charCodeAt(i) + hash * 31; // Replace << 5 - with * 31 to avoid bitwise operations
    }

    // Convert the hash to an index within the range of TagV2ColorsEnum
    const colorKeys = Object.keys(TagV2ColorsEnum) as TagV2Colors[];
    const index = Math.abs(hash) % colorKeys.length;

    // Return the color corresponding to the index
    return colorKeys[index];
};

export const TagV2 = forwardRef<HTMLDivElement, TagV2Props>((props, ref): JSX.Element => {
    const { children, iconRight: IconRight, onIconClick, displayIconOnHover, onClick, color, disableTooltip, ...rest } = props;
    const theme = useTheme();

    const tagColor = color || getRandomTagColor(children?.toString() || "");
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const hasIcon = Boolean(IconRight);
    const isIconVisible = hasIcon && (isHovered || !displayIconOnHover);

    return (
        <StyledTag
            showIcon={hasIcon}
            ref={ref}
            color={tagColor}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={onClick}
            {...rest}
        >
            {typeof children === "string" ? (
                <TextWrapper>
                    <Text size={TextSize["XX-SMALL"]} weight={TextWeight.SEMIBOLD} color={theme.grayscale.white}>
                        <TooltipOnTextOverflow disabled={disableTooltip}>{children}</TooltipOnTextOverflow>
                    </Text>
                </TextWrapper>
            ) : (
                children
            )}
            {IconRight && (
                <IconWrapper visible={isIconVisible}>
                    <IconBox backgroundColor="transparent" padding="4px" onClick={onIconClick}>
                        <IconRight size={ICONSAX_SIZE.SMALL} />
                    </IconBox>
                </IconWrapper>
            )}
        </StyledTag>
    );
});
